import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, Trash2, Server, ExternalLink } from "lucide-react";
import {
  addBackend,
  createPool,
  deletePool,
  listPools,
  removeBackend,
  type LBBackend,
  type LBBackendCreate,
  type LBPool,
  type LBPoolCreate,
} from "@/api/endpoints/lb";
import { getVMs } from "@/api/endpoints/vms";
import { useActiveTenant } from "@/components/tenant/TenantSelector";
import type { VM } from "@/types";

export default function LoadBalancer() {
  const qc = useQueryClient();
  const [tenantId] = useActiveTenant();
  const { data: pools, isLoading, error, refetch } = useQuery({
    queryKey: ["lb", "pools"],
    queryFn: listPools,
    refetchInterval: 15_000,
  });
  const { data: vms } = useQuery({
    queryKey: ["vms", tenantId],
    queryFn: () => getVMs(tenantId ?? undefined),
  });
  const vmById = useMemo(() => {
    const map = new Map<number, VM>();
    (vms ?? []).forEach((v) => map.set(v.id, v));
    return map;
  }, [vms]);

  const [createOpen, setCreateOpen] = useState(false);

  const createMut = useMutation({
    mutationFn: createPool,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lb", "pools"] });
      setCreateOpen(false);
    },
  });
  const deleteMut = useMutation({
    mutationFn: deletePool,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lb", "pools"] }),
  });
  const addBackendMut = useMutation({
    mutationFn: ({ poolId, payload }: { poolId: number; payload: LBBackendCreate }) =>
      addBackend(poolId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lb", "pools"] }),
  });
  const removeBackendMut = useMutation({
    mutationFn: ({ poolId, backendId }: { poolId: number; backendId: number }) =>
      removeBackend(poolId, backendId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lb", "pools"] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Load balancer</h1>
          <p className="mt-1 text-sm text-gray-400">
            HAProxy pools distributing traffic across VMs and external endpoints.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500"
          >
            <Plus className="h-4 w-4" />
            New pool
          </button>
        </div>
      </div>

      {isLoading && <div className="text-gray-400">Loading pools…</div>}
      {error && (
        <div className="rounded-lg border border-red-600/40 bg-red-600/10 p-4 text-red-300">
          Failed to load pools: {String(error)}
        </div>
      )}

      {pools && pools.length === 0 && (
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-6 text-center text-gray-400">
          No pools defined yet. Create one to start load-balancing traffic.
        </div>
      )}

      <div className="space-y-4">
        {pools?.map((p) => (
          <PoolCard
            key={p.id}
            pool={p}
            vms={vms ?? []}
            vmById={vmById}
            onDelete={() => deleteMut.mutate(p.id)}
            onAddBackend={(payload) =>
              addBackendMut.mutate({ poolId: p.id, payload })
            }
            onRemoveBackend={(backendId) =>
              removeBackendMut.mutate({ poolId: p.id, backendId })
            }
          />
        ))}
      </div>

      {createOpen && (
        <CreatePoolModal
          onClose={() => setCreateOpen(false)}
          onSubmit={(payload) => createMut.mutate(payload)}
          pending={createMut.isPending}
          error={createMut.error ? String(createMut.error) : null}
        />
      )}
    </div>
  );
}

function PoolCard({
  pool,
  vms,
  vmById,
  onDelete,
  onAddBackend,
  onRemoveBackend,
}: {
  pool: LBPool;
  vms: VM[];
  vmById: Map<number, VM>;
  onDelete: () => void;
  onAddBackend: (payload: LBBackendCreate) => void;
  onRemoveBackend: (backendId: number) => void;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const disabledBadge = pool.enabled ? (
    <span className="rounded-full bg-emerald-600/20 px-2 py-0.5 text-xs font-medium text-emerald-400">
      enabled
    </span>
  ) : (
    <span className="rounded-full bg-gray-700 px-2 py-0.5 text-xs text-gray-300">
      disabled
    </span>
  );

  return (
    <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-100">{pool.name}</h2>
            {disabledBadge}
          </div>
          <p className="mt-1 text-sm text-gray-400">
            {pool.protocol.toUpperCase()} · listen :{pool.listen_port} · {pool.algorithm}
            {" · "}
            health: {pool.health_check_type}
            {pool.health_check_type === "http" && pool.health_check_path
              ? ` ${pool.health_check_path}`
              : ""}
            {" · every "}
            {pool.health_check_interval_seconds}s
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAddOpen(true)}
            className="flex items-center gap-1 rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-200 hover:bg-gray-700"
          >
            <Plus className="h-3 w-3" /> backend
          </button>
          <button
            onClick={() => {
              if (confirm(`Delete pool ${pool.name}?`)) onDelete();
            }}
            className="rounded border border-red-700/50 bg-red-700/10 px-2 py-1 text-xs text-red-300 hover:bg-red-700/20"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      <div className="mt-4 divide-y divide-gray-800 rounded border border-gray-800">
        {pool.backends.length === 0 && (
          <div className="px-3 py-2 text-sm text-gray-500">No backends yet.</div>
        )}
        {pool.backends.map((b) => (
          <BackendRow
            key={b.id}
            backend={b}
            vm={b.vm_id ? vmById.get(b.vm_id) : undefined}
            onRemove={() => onRemoveBackend(b.id)}
          />
        ))}
      </div>

      {addOpen && (
        <AddBackendModal
          vms={vms}
          existingVmIds={new Set(
            pool.backends.map((b) => b.vm_id).filter((v): v is number => v !== null),
          )}
          defaultPort={pool.listen_port}
          onClose={() => setAddOpen(false)}
          onSubmit={(payload) => {
            onAddBackend(payload);
            setAddOpen(false);
          }}
        />
      )}
    </div>
  );
}

function BackendRow({
  backend,
  vm,
  onRemove,
}: {
  backend: LBBackend;
  vm: VM | undefined;
  onRemove: () => void;
}) {
  const isManaged = backend.vm_id !== null;
  return (
    <div className="flex items-center justify-between px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        {isManaged ? (
          <Server className="h-4 w-4 flex-shrink-0 text-blue-400" />
        ) : (
          <ExternalLink className="h-4 w-4 flex-shrink-0 text-gray-400" />
        )}
        {isManaged && vm && (
          <span className="rounded bg-blue-600/20 px-1.5 py-0.5 text-xs font-medium text-blue-300">
            {vm.name}
          </span>
        )}
        {isManaged && !vm && (
          <span
            className="rounded bg-amber-600/20 px-1.5 py-0.5 text-xs text-amber-300"
            title="Linked VM not visible (different tenant or deleted)"
          >
            vm #{backend.vm_id}
          </span>
        )}
        <span className="font-mono text-gray-200">
          {backend.backend_host}:{backend.backend_port}
        </span>
        <span className="text-xs text-gray-500">weight {backend.weight}</span>
        {!backend.enabled && (
          <span className="rounded bg-gray-700 px-1.5 py-0.5 text-xs text-gray-300">
            disabled
          </span>
        )}
      </div>
      <button
        onClick={() => {
          if (confirm(`Remove ${backend.backend_host}:${backend.backend_port}?`))
            onRemove();
        }}
        className="text-gray-500 hover:text-red-400"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

function CreatePoolModal({
  onClose,
  onSubmit,
  pending,
  error,
}: {
  onClose: () => void;
  onSubmit: (p: LBPoolCreate) => void;
  pending: boolean;
  error: string | null;
}) {
  const [name, setName] = useState("");
  const [protocol, setProtocol] = useState<"tcp" | "http">("http");
  const [listenPort, setListenPort] = useState(80);
  const [algorithm, setAlgorithm] = useState<"roundrobin" | "leastconn" | "source">(
    "roundrobin",
  );
  const [hcType, setHcType] = useState<"tcp" | "http">("tcp");
  const [hcPath, setHcPath] = useState("/");
  const [hcInterval, setHcInterval] = useState(5);

  return (
    <Modal title="Create pool" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({
            name,
            protocol,
            listen_port: listenPort,
            algorithm,
            health_check_type: hcType,
            health_check_path: hcType === "http" ? hcPath : null,
            health_check_interval_seconds: hcInterval,
          });
        }}
        className="space-y-3"
      >
        <Field label="Name">
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputCls}
            placeholder="web-acme"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Protocol">
            <select
              value={protocol}
              onChange={(e) => setProtocol(e.target.value as "tcp" | "http")}
              className={inputCls}
            >
              <option value="http">HTTP</option>
              <option value="tcp">TCP</option>
            </select>
          </Field>
          <Field label="Listen port">
            <input
              type="number"
              required
              min={1}
              max={65535}
              value={listenPort}
              onChange={(e) => setListenPort(Number(e.target.value))}
              className={inputCls}
            />
          </Field>
        </div>
        <Field label="Algorithm">
          <select
            value={algorithm}
            onChange={(e) => setAlgorithm(e.target.value as typeof algorithm)}
            className={inputCls}
          >
            <option value="roundrobin">Round robin</option>
            <option value="leastconn">Least connections</option>
            <option value="source">Source-IP hash</option>
          </select>
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Health check">
            <select
              value={hcType}
              onChange={(e) => setHcType(e.target.value as "tcp" | "http")}
              className={inputCls}
            >
              <option value="tcp">TCP</option>
              <option value="http">HTTP</option>
            </select>
          </Field>
          <Field label="HTTP path">
            <input
              value={hcPath}
              disabled={hcType !== "http"}
              onChange={(e) => setHcPath(e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Interval (s)">
            <input
              type="number"
              min={1}
              max={300}
              value={hcInterval}
              onChange={(e) => setHcInterval(Number(e.target.value))}
              className={inputCls}
            />
          </Field>
        </div>
        {error && <div className="text-sm text-red-400">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary}>
            Cancel
          </button>
          <button type="submit" disabled={pending} className={btnPrimary}>
            {pending ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function AddBackendModal({
  vms,
  existingVmIds,
  defaultPort,
  onClose,
  onSubmit,
}: {
  vms: VM[];
  existingVmIds: Set<number>;
  defaultPort: number;
  onClose: () => void;
  onSubmit: (p: LBBackendCreate) => void;
}) {
  const pickableVMs = useMemo(
    () =>
      vms
        .filter((v) => v.ip_address && !existingVmIds.has(v.id))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [vms, existingVmIds],
  );

  const [mode, setMode] = useState<"vm" | "external">(
    pickableVMs.length > 0 ? "vm" : "external",
  );
  const [vmId, setVmId] = useState<number | null>(pickableVMs[0]?.id ?? null);
  const [host, setHost] = useState("");
  const [port, setPort] = useState(defaultPort);
  const [weight, setWeight] = useState(10);

  const selectedVM: VM | null =
    mode === "vm" && vmId !== null
      ? (vms.find((v) => v.id === vmId) ?? null)
      : null;
  const resolvedHost = selectedVM?.ip_address ?? host;

  const canSubmit =
    mode === "external"
      ? host.trim().length > 0
      : selectedVM !== null && selectedVM.ip_address !== null;

  return (
    <Modal title="Add backend" onClose={onClose}>
      <div className="mb-4 flex rounded border border-gray-700 bg-gray-800 p-1 text-sm">
        <SegmentButton
          active={mode === "vm"}
          disabled={pickableVMs.length === 0}
          onClick={() => setMode("vm")}
        >
          <Server className="mr-1.5 inline h-3.5 w-3.5" /> Managed VM
        </SegmentButton>
        <SegmentButton active={mode === "external"} onClick={() => setMode("external")}>
          <ExternalLink className="mr-1.5 inline h-3.5 w-3.5" /> External host
        </SegmentButton>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          if (mode === "vm" && selectedVM && selectedVM.ip_address) {
            onSubmit({
              vm_id: selectedVM.id,
              backend_host: selectedVM.ip_address,
              backend_port: port,
              weight,
            });
          } else {
            onSubmit({
              backend_host: host,
              backend_port: port,
              weight,
            });
          }
        }}
        className="space-y-3"
      >
        {mode === "vm" ? (
          <>
            {pickableVMs.length === 0 ? (
              <div className="rounded border border-amber-700/40 bg-amber-600/10 p-3 text-sm text-amber-300">
                No managed VM is available to add to this pool — either every VM
                is already here, or none has an allocated IP yet. Switch to
                "External host" instead.
              </div>
            ) : (
              <Field label="Virtual machine">
                <select
                  value={vmId ?? ""}
                  onChange={(e) => setVmId(Number(e.target.value))}
                  className={inputCls}
                >
                  {pickableVMs.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} — {v.ip_address} ({v.node}, Tier {v.service_tier})
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {selectedVM && (
              <div className="text-xs text-gray-500">
                Resolved endpoint:{" "}
                <span className="font-mono text-gray-300">
                  {resolvedHost}:{port}
                </span>
              </div>
            )}
          </>
        ) : (
          <Field label="Host / IP">
            <input
              required
              value={host}
              onChange={(e) => setHost(e.target.value)}
              className={inputCls}
              placeholder="10.240.0.5 or api.example.com"
            />
          </Field>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Port">
            <input
              type="number"
              required
              min={1}
              max={65535}
              value={port}
              onChange={(e) => setPort(Number(e.target.value))}
              className={inputCls}
            />
          </Field>
          <Field label="Weight">
            <input
              type="number"
              min={0}
              max={256}
              value={weight}
              onChange={(e) => setWeight(Number(e.target.value))}
              className={inputCls}
            />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary}>
            Cancel
          </button>
          <button type="submit" disabled={!canSubmit} className={btnPrimary}>
            Add
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SegmentButton({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={
        "flex-1 rounded px-3 py-1.5 text-sm transition-colors " +
        (active
          ? "bg-blue-600 text-white"
          : "text-gray-300 hover:text-white disabled:text-gray-600 disabled:hover:text-gray-600")
      }
    >
      {children}
    </button>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-gray-800 bg-gray-900 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-4 text-lg font-semibold text-gray-100">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="text-gray-400">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const inputCls =
  "w-full rounded border border-gray-700 bg-gray-800 px-2 py-1.5 text-sm text-gray-100 focus:border-blue-500 focus:outline-none disabled:opacity-50";
const btnPrimary =
  "rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50";
const btnSecondary =
  "rounded border border-gray-700 bg-gray-800 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700";
